import { Action, ActionReducer, createReducer, on } from '@ngrx/store';

import { UserDetailsActions } from './user-details.actions';
import { UserDetailsState } from './user-details.state';

export const initUserDetailsState: UserDetailsState = {
  userDetails: null,
  user: null,
  company: null,
  installations: [],
  fetchMoreInstallationsCursor: '',
  fetchInstallationsError: null,
  loadingInstallations: true,
  loading: true,
  loaded: false,
  saving: false,
  saved: false,
  error: null,
};

export const userDetailsReducer: ActionReducer<UserDetailsState, Action> = createReducer(
  initUserDetailsState,
  on(
    UserDetailsActions.getUserDetails,
    (_state): UserDetailsState => ({ ...initUserDetailsState })
  ),

  on(
    UserDetailsActions.getUserDetailsSuccess,
    (state, { response }): UserDetailsState => ({ ...state, userDetails: response.userDetails })
  ),

  on(
    UserDetailsActions.getUserDetailsError,
    (state, { error }): UserDetailsState => ({
      ...state,
      userDetails: null,
      user: null,
      installations: null,
      loadingInstallations: false,
      loading: false,
      loaded: false,
      error,
    })
  ),

  on(
    UserDetailsActions.readUserDetailsSuccess,
    UserDetailsActions.verifyIfUserIsOma.success,
    (state, { user }): UserDetailsState => ({
      ...state,
      user,
      loading: false,
      loaded: true,
      error: null,
    })
  ),

  on(
    UserDetailsActions.readUserDetailsError,
    (state): UserDetailsState => ({ ...state, loading: false, loaded: true })
  ),

  on(
    UserDetailsActions.changeUserDetails,
    (state): UserDetailsState => ({ ...state, saving: true })
  ),

  on(
    UserDetailsActions.initUserDetailsChange,
    (state): UserDetailsState => ({ ...state, saving: false, saved: false, error: null })
  ),

  on(
    UserDetailsActions.changeUserDetailsSuccess,
    (state, { body }): UserDetailsState => {
      let userDetails = { ...state.userDetails };
      if (body.userState) {
        userDetails = {
          ...userDetails,
          userState: body.userState,
        };
      }

      if (body.name?.firstName) {
        userDetails = {
          ...userDetails,
          name: {
            ...userDetails.name,
            firstName: body.name.firstName,
          },
        };
      }

      if (body.name?.familyName) {
        userDetails = {
          ...userDetails,
          name: {
            ...userDetails.name,
            familyName: body.name.familyName,
          },
        };
      }

      if (body.gender) {
        userDetails.gender = body.gender;
      }

      if (body.contacts?.telephone || body.contacts?.telephone === '') {
        userDetails = {
          ...userDetails,
          contacts: {
            ...userDetails.contacts,
            telephone: body.contacts.telephone,
          },
        };
      }

      if (body.contacts?.mobile || body.contacts?.mobile === '') {
        userDetails = {
          ...userDetails,
          contacts: {
            ...userDetails.contacts,
            mobile: body.contacts.mobile,
          },
        };
      }

      if (body.contacts?.telefax || body.contacts?.telefax === '') {
        userDetails = {
          ...userDetails,
          contacts: {
            ...userDetails.contacts,
            telefax: body.contacts.telefax,
          },
        };
      }

      if (body.address?.street || body.address?.street === '') {
        userDetails = {
          ...userDetails,
          address: {
            ...userDetails.address,
            street: body.address.street,
          },
        };
      }

      if (body.address?.houseNumber || body.address?.houseNumber === '') {
        userDetails = {
          ...userDetails,
          address: {
            ...userDetails.address,
            houseNumber: body.address.houseNumber,
          },
        };
      }

      if (body.address?.postalCode || body.address?.postalCode === '') {
        userDetails = {
          ...userDetails,
          address: {
            ...userDetails.address,
            postalCode: body.address.postalCode,
          },
        };
      }

      if (body.address?.city || body.address?.city === '') {
        userDetails = {
          ...userDetails,
          address: {
            ...userDetails.address,
            city: body.address.city,
          },
        };
      }

      if (body.address?.countryCode || body.address?.countryCode === '') {
        userDetails = {
          ...userDetails,
          address: {
            ...userDetails.address,
            countryCode: body.address.countryCode,
          },
        };
      }

      if (body.address?.locality || body.address?.locality === '') {
        userDetails = {
          ...userDetails,
          address: {
            ...userDetails.address,
            locality: body.address.locality,
          },
        };
      }

      if (body.languageCode) {
        userDetails.languageCode = body.languageCode;
        userDetails = {
          ...userDetails,
          languageCode: body.languageCode,
        };
      }

      return {
        ...state,
        userDetails,
        saving: false,
        saved: true,
      };
    }
  ),

  on(
    UserDetailsActions.changeUserDetailsError,
    (state, { error }): UserDetailsState => ({
      ...state,
      saving: false,
      saved: false,
      error,
    })
  ),

  on(
    UserDetailsActions.changeOmaUserDetails.attempt,
    (state): UserDetailsState => {
      return {
        ...state,
        saving: true,
        saved: false,
        error: null,
      };
    }
  ),

  on(
    UserDetailsActions.changeOmaUserDetails.success,
    (state, { user }): UserDetailsState => {
      return {
        ...state,
        user,
        saving: false,
        saved: true,
      };
    }
  ),

  on(
    UserDetailsActions.changeOmaUserDetails.error,
    (state, { error }): UserDetailsState => {
      return {
        ...state,
        saving: false,
        saved: false,
        error,
      };
    }
  ),

  on(
    UserDetailsActions.closeChangeUserDetailsModal,
    (state): UserDetailsState => ({ ...state, saving: false, saved: false })
  ),

  on(
    UserDetailsActions.getUserInstallations,
    (state): UserDetailsState => ({ ...state, fetchInstallationsError: null })
  ),

  on(
    UserDetailsActions.getUserInstallationsSuccess,
    (state, { installations, cursor }): UserDetailsState => ({
      ...state,
      installations: [].concat(state.installations, installations),
      fetchMoreInstallationsCursor: cursor,
      loadingInstallations: false,
      fetchInstallationsError: null,
    })
  ),

  on(
    UserDetailsActions.getUserInstallationsError,
    (state, { error }): UserDetailsState => ({
      ...state,
      loadingInstallations: false,
      fetchMoreInstallationsCursor: '',
      fetchInstallationsError: error,
    })
  ),

  on(UserDetailsActions.getUserCompany, (state): UserDetailsState => ({ ...state, company: null })),

  on(
    UserDetailsActions.getUserCompanySuccess,
    (state, { company }): UserDetailsState => ({ ...state, company })
  ),

  on(
    UserDetailsActions.getUserCompanyError,
    (state, { error }): UserDetailsState => ({ ...state, company: null, error })
  )
);
